import * as React from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import {
  getWidgetProps,
  WPSClient,
  Args,
  withArgs,
  Widget
} from 'DVWebNodeUtils'

import { requestWpsData } from '../../utils/requestWpsData'

import * as st from '../../components/utilityClasses.module.scss'

import { Container } from '@amzn/dvui/dist/Container'
import { BtfProductMetaInfo } from '../../components/productMetaInfo/btfProductMetaInfo'
import { StringBundle } from '../../components/localizer/localizer'
import { configureStore } from '../../store'
import {
  normalizeGetDetailPage, NormalizeGetDetailPageType,
  normalizeGetStrings, NormalizeGetStringsType
} from '../../data/normalize'
import { IDetailPageState } from '../../reducers'
import { getAdditionalInput } from '../../data/getDetailPageInputs'
import { Faq } from '../../components/faq/faq'
import { ContactUs } from '../../components/contactUs/contactUs'
import { safeBoolean } from '../../utils/utils'
import { Carousel } from '../../components/carousel/carousel'
import { ScoutBanner } from '../../components/scoutBanner/scoutBanner'
import { CastAndCrew } from '../../components/castAndCrew/castAndCrew'
import { ErrorBoundary } from '../../components/error/ErrorBoundary'
import { blockedPageSelector } from '../../selectors/component/pageBlockComponentSelectors'
import { OtherFormats } from '../../components/otherFormats/otherFormats'


export interface AdditionalProps {
  state:
    NormalizeGetDetailPageType &
    NormalizeGetStringsType &
    Pick<IDetailPageState, 'features'>
}

export const getProps: getWidgetProps<AdditionalProps> = async ({ context, titleID, useFaq, useContactUs, usePlayer, useSixteenByNine }, api) => {
  const operation = (wpsClient: WPSClient) =>
      wpsClient.GetDetailPage(getAdditionalInput({ context, titleID }, safeBoolean(useFaq), safeBoolean(useContactUs)))

  const reply = await requestWpsData({
    context,
    api,
    operation,
    stringBundle: StringBundle.BTF
  })

  const features = {
    useFaq: safeBoolean(useFaq),
    useContactUs: safeBoolean(useContactUs),
    usePlayer: safeBoolean(usePlayer),
    useSixteenByNine: safeBoolean(useSixteenByNine)
  }

  return {
    state: {
      features,
      ...normalizeGetDetailPage(titleID, reply.operationResponse),
      ...normalizeGetStrings(reply.stringsResponse!)
    }
  }
}

const AdditionalWidget = (props: AdditionalProps & Args) => {
  const { logger, state, context: requestContext } = props
  const store = configureStore({ ...state, requestContext }, logger)

  const faq = store.getState().faq
  const contactUs = store.getState().contactUs

  // Page block prevents any render in btf-additional. Message will be shown in atf
  const pageBlock = blockedPageSelector(store.getState())
  if (pageBlock) {
    return null
  }

  return (
    <ReduxProvider store={store}>
      <ErrorBoundary name="additional">
        <Carousel />
        {!global.__NATIVEAPP__ && <ScoutBanner content={state.scoutBanner[state.pageTitleId]} />}
        <CastAndCrew content={state.castAndCrew[state.pageTitleId]} />
        <Container className={st.pageSection}>
          <BtfProductMetaInfo />
        </Container>
        {state.features.useFaq && <Faq content={faq[state.pageTitleId]} />}
        {state.features.useContactUs && (
          <ContactUs content={contactUs[state.pageTitleId]} />
        )}
        <Container className={st.pageSection}>
          <OtherFormats content={state.otherFormats[state.pageTitleId]}/>
        </Container>
      </ErrorBoundary>
    </ReduxProvider>
  )
}

export const Additional: Widget<AdditionalProps> = {
  name: 'detail-additional',
  getProps: global.__SERVER__ === true ? getProps : undefined,
  Component: withArgs(AdditionalWidget) as React.ComponentType<any>
}
